if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.cachedProductHTML = null;

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        if (!this.modalContent) this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        if (this.modalContent) this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        if (!this.modalContent) this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        if (!this.modalContent) {
          console.error('[theme] Quick-add modal content is missing');
          return;
        }

        if (this.hasAttribute('open')) return;

        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');

        this.modalContent.setAttribute('aria-busy', 'true');
        this.modalContent.innerHTML = `
          <div class="quick-add-modal__loading" role="status" aria-live="polite">
            <span class="quick-add-modal__loading-spinner" aria-hidden="true"></span>
            <span>Loading product options…</span>
          </div>
        `;
        super.show(opener);

        const productRequest = this.cachedProductHTML
          ? Promise.resolve(this.cachedProductHTML)
          : fetch(opener.getAttribute('data-product-url'))
              .then((response) => {
                if (!response.ok) throw new Error(`Quick-add request failed (${response.status})`);
                return response.text();
              })
              .then((responseText) => {
                this.cachedProductHTML = responseText;
                return responseText;
              });

        productRequest
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('Quick-add product content is missing');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();
          })
          .catch((error) => {
            console.error('[theme] Quick-add could not load', error);
            this.modalContent.innerHTML = `
              <div class="quick-add-modal__error" role="alert">
                Product options couldn’t load. Close this window and try again.
              </div>
            `;
          })
          .finally(() => {
            this.modalContent.removeAttribute('aria-busy');
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        // The Section Rendering API uses `main-product` as the section ID.
        // Keep that token in asset URLs so filenames such as
        // `section-main-product.css` are not rewritten to non-existent files.
        productElement.querySelectorAll('link[href], script[src]').forEach((asset) => {
          ['href', 'src'].forEach((attributeName) => {
            const attributeValue = asset.getAttribute(attributeName);
            if (attributeValue?.includes(newId)) {
              asset.setAttribute(attributeName, attributeValue.replaceAll(newId, oldId));
            }
          });
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
